import { CONTRASTE_MINIMO, juzgarContraste } from '@qr/shared/contrast';
import { fileUrl } from '@qr/shared/storage';
import type { QrDesign } from '@qr/shared/types';
import { Button, Callout } from '@qr/ui';
import { useRef, useState } from 'react';
import { api, messageFromError } from '../lib/api.js';
import { R2_PUBLIC_BASE } from '../lib/firebase.js';

/**
 * Personalizacion visual del codigo.
 *
 * Lo que se codifica no cambia: siempre es el enlace corto. Lo que cambia es
 * como se dibuja, y ahi hay una sola cosa que puede romperlo de verdad, el
 * contraste. Por eso el contraste no es un aviso decorativo sino una condicion
 * para guardar: un codigo bonito que no se lee llega a imprenta y el fallo
 * aparece en la mesa del cliente, no aqui.
 *
 * El logo va en el centro y tapa modulos. El codigo se genera siempre con
 * correccion de errores en nivel H (recupera hasta un 30%), que es lo que hace
 * que siga leyendose con el hueco tapado.
 */

const COLORES_SUGERIDOS = [
  { valor: '#101418', nombre: 'Casi negro' },
  { valor: '#1a3a5c', nombre: 'Azul marino' },
  { valor: '#0f3d2e', nombre: 'Verde bosque' },
  { valor: '#5c1a2e', nombre: 'Granate' },
  { valor: '#3d2b1f', nombre: 'Marron' },
  { valor: '#2d1b4e', nombre: 'Morado' },
];

const FONDOS_SUGERIDOS = [
  { valor: '#ffffff', nombre: 'Blanco' },
  { valor: '#faf8f5', nombre: 'Hueso' },
  { valor: '#f2f4f7', nombre: 'Gris claro' },
];

const MODULOS = [
  { valor: 'square', nombre: 'Cuadrados' },
  { valor: 'rounded', nombre: 'Redondeados' },
  { valor: 'dots', nombre: 'Puntos' },
] as const;

const OJOS = [
  { valor: 'square', nombre: 'Cuadrados' },
  { valor: 'rounded', nombre: 'Redondeados' },
  { valor: 'circle', nombre: 'Circulos' },
] as const;

const LOGO_TIPOS = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const LOGO_MAXIMO = 2 * 1024 * 1024;

/**
 * Lado minimo recomendado del logo.
 *
 * El codigo se descarga a 1024 pixeles y el logo ocupa en el un tercio, unos
 * 340. Un archivo por debajo de eso se estira y se ve pixelado justo en el
 * centro, que es donde primero mira la gente.
 */
const LOGO_LADO_MINIMO = 400;

/**
 * Mide el archivo antes de subirlo para poder avisar de lo que ningun ajuste
 * arregla: un original pequeno sale borroso, y uno apaisado deja bandas
 * blancas porque el hueco que se despeja en el centro del codigo es cuadrado.
 */
function medir(archivo: File): Promise<{ ancho: number; alto: number } | null> {
  // Un SVG es vectorial: no tiene resolucion que criticar.
  if (archivo.type === 'image/svg+xml') return Promise.resolve(null);

  return new Promise((resolve) => {
    const url = URL.createObjectURL(archivo);
    const img = new Image();
    img.addEventListener('load', () => {
      URL.revokeObjectURL(url);
      resolve({ ancho: img.naturalWidth, alto: img.naturalHeight });
    });
    img.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      resolve(null);
    });
    img.src = url;
  });
}

function pegaDelLogo(medida: { ancho: number; alto: number } | null): string | null {
  if (!medida) return null;

  const { ancho, alto } = medida;
  const lado = Math.max(ancho, alto);
  const proporcion = Math.max(ancho, alto) / Math.min(ancho, alto);

  if (lado < LOGO_LADO_MINIMO && proporcion > 1.4) {
    return `Tu logo mide ${ancho}x${alto} px. Se vera pixelado en el centro del codigo, y al no ser cuadrado deja franjas vacias a los lados. Lo ideal: cuadrado y de al menos ${LOGO_LADO_MINIMO} px.`;
  }
  if (lado < LOGO_LADO_MINIMO) {
    return `Tu logo mide ${ancho}x${alto} px y se vera pixelado al imprimir el codigo. Sube uno de al menos ${LOGO_LADO_MINIMO} px de lado.`;
  }
  if (proporcion > 1.4) {
    return `Tu logo es ${ancho}x${alto} px, bastante mas ancho que alto. El hueco que se despeja en el centro del codigo es cuadrado, asi que quedaran franjas vacias arriba y abajo. Una version cuadrada aprovecha todo el espacio.`;
  }
  return null;
}

export function DesignEditor({
  orgId,
  value,
  disabled = false,
  onChange,
}: {
  readonly orgId: string;
  readonly value: QrDesign;
  readonly disabled?: boolean;
  readonly onChange: (design: QrDesign) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [errorLogo, setErrorLogo] = useState<string | null>(null);
  const [pegaLogo, setPegaLogo] = useState<string | null>(null);

  const veredicto = juzgarContraste(value.foreground, value.background);

  // La ruta manda; la direccion entera solo la tienen los codigos antiguos.
  const logoVisible = fileUrl(value.logoKey ?? value.logoUrl, R2_PUBLIC_BASE);

  async function subirLogo(archivo: File) {
    setErrorLogo(null);
    setPegaLogo(null);

    if (!LOGO_TIPOS.includes(archivo.type)) {
      setErrorLogo('El logo tiene que ser PNG, JPG, WEBP o SVG.');
      return;
    }
    if (archivo.size > LOGO_MAXIMO) {
      setErrorLogo('El logo no puede pasar de 2 MB.');
      return;
    }

    // Se mide antes de subir, pero no se bloquea: es un consejo, no un error.
    // Un logo pequeno sigue siendo mejor que ninguno si es lo unico que hay.
    setPegaLogo(pegaDelLogo(await medir(archivo)));

    setSubiendo(true);
    try {
      const permiso = await api.createUploadUrl({
        orgId,
        contentType: archivo.type,
        size: archivo.size,
        purpose: 'logo',
      });

      const respuesta = await fetch(permiso.uploadUrl, {
        method: 'PUT',
        headers: permiso.headers,
        body: archivo,
      });
      if (!respuesta.ok) {
        throw new Error(`El almacenamiento respondio ${respuesta.status}`);
      }

      // Se guarda la RUTA. La direccion se compone al pintarlo, asi que el
      // dia que cambie el dominio del almacenamiento este codigo no hay que
      // tocarlo. `logoUrl` se limpia: si quedara, ganaria a la ruta al leer.
      onChange({ ...value, logoKey: permiso.key, logoUrl: null });
    } catch (fallo) {
      console.error('No se pudo subir el logo', fallo);
      setErrorLogo(messageFromError(fallo));
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <div className="diseno">
      <Paleta
        etiqueta="Color del codigo"
        pista="Tiene que ser el tono oscuro de los dos."
        opciones={COLORES_SUGERIDOS}
        valor={value.foreground}
        disabled={disabled}
        onChange={(foreground) => onChange({ ...value, foreground })}
      />

      <Paleta
        etiqueta="Fondo"
        pista="Claro. Un codigo claro sobre fondo oscuro no lo leen muchos telefonos."
        opciones={FONDOS_SUGERIDOS}
        valor={value.background}
        disabled={disabled}
        onChange={(background) => onChange({ ...value, background })}
      />

      <AvisoContraste veredicto={veredicto} />

      <Formas
        etiqueta="Forma de los modulos"
        opciones={MODULOS}
        valor={value.moduleShape}
        disabled={disabled}
        onChange={(moduleShape) => onChange({ ...value, moduleShape })}
      />

      <Formas
        etiqueta="Forma de las esquinas"
        opciones={OJOS}
        valor={value.eyeShape}
        disabled={disabled}
        onChange={(eyeShape) => onChange({ ...value, eyeShape })}
      />

      <div className="qr-field">
        <span className="qr-field__label">Logo</span>

        {logoVisible ? (
          <div className="archivo">
            <img className="diseno__logo" src={logoVisible} alt="Logo del código" />
            <div className="archivo__datos">
              <span className="muted small">Se incrusta en el centro del código.</span>
            </div>
            <Button
              size="sm"
              variant="secondary"
              disabled={disabled || subiendo}
              onClick={() => input.current?.click()}
            >
              Cambiar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled || subiendo}
              onClick={() => onChange({ ...value, logoKey: null, logoUrl: null })}
            >
              Quitar
            </Button>
          </div>
        ) : (
          <Button
            variant="secondary"
            disabled={disabled || subiendo}
            loading={subiendo}
            onClick={() => input.current?.click()}
          >
            Subir logo
          </Button>
        )}

        <input
          ref={input}
          type="file"
          accept={LOGO_TIPOS.join(',')}
          hidden
          onChange={(e) => {
            const archivo = e.target.files?.[0];
            e.target.value = '';
            if (archivo) void subirLogo(archivo);
          }}
        />

        <p className="qr-field__hint">
          PNG, JPG, WEBP o SVG, hasta 2 MB. Cuadrado y de al menos{' '}
          {LOGO_LADO_MINIMO} px: ocupa el centro del codigo y ahi se nota
          cualquier borrosidad. Con fondo transparente queda mejor.
        </p>
      </div>

      {errorLogo ? <Callout tone="danger">{errorLogo}</Callout> : null}
      {pegaLogo ? (
        <Callout tone="warning" title="Sobre este logo">
          {pegaLogo}
        </Callout>
      ) : null}
    </div>
  );
}

/** Si el par de colores permite guardar. Lo usa el formulario que lo contiene. */
export function disenoValido(design: QrDesign): boolean {
  return juzgarContraste(design.foreground, design.background).nivel !== 'insuficiente';
}

function AvisoContraste({
  veredicto,
}: {
  readonly veredicto: ReturnType<typeof juzgarContraste>;
}) {
  if (veredicto.nivel === 'bueno') return null;

  if (veredicto.nivel === 'invalido') {
    return <Callout tone="danger">Ese color no es válido.</Callout>;
  }

  const ratio = veredicto.ratio.toFixed(1);

  if (veredicto.nivel === 'justo') {
    return (
      <Callout tone="warning" title="El contraste justo">
        Contraste {ratio}:1. Se lee, pero sin margen: con poca luz, una
        impresion mediocre o una camara vieja puede fallar. Un tono mas oscuro
        lo resuelve.
      </Callout>
    );
  }

  return (
    <Callout tone="danger" title="Este código no se va a leer">
      Contraste {ratio}:1, y hace falta al menos {CONTRASTE_MINIMO}:1. Elige un
      color de codigo mas oscuro o un fondo mas claro. No dejamos guardarlo
      porque el fallo aparece cuando ya esta impreso.
    </Callout>
  );
}

function Paleta({
  etiqueta,
  pista,
  opciones,
  valor,
  disabled,
  onChange,
}: {
  readonly etiqueta: string;
  readonly pista: string;
  readonly opciones: readonly { valor: string; nombre: string }[];
  readonly valor: string;
  readonly disabled: boolean;
  readonly onChange: (valor: string) => void;
}) {
  return (
    <div className="qr-field">
      <span className="qr-field__label">{etiqueta}</span>
      <div className="diseno__paleta">
        {opciones.map((opcion) => (
          <button
            key={opcion.valor}
            type="button"
            className="diseno__muestra"
            style={{ background: opcion.valor }}
            aria-label={opcion.nombre}
            aria-pressed={valor.toLowerCase() === opcion.valor}
            disabled={disabled}
            onClick={() => onChange(opcion.valor)}
          />
        ))}
        {/* El selector nativo cubre cualquier color de marca que no este en la
            lista, sin obligar a escribir un codigo hexadecimal a mano. */}
        <label className="diseno__personalizado">
          <input
            type="color"
            value={valor}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          <span className="muted small">Otro</span>
        </label>
      </div>
      <p className="qr-field__hint">{pista}</p>
    </div>
  );
}

function Formas<T extends string>({
  etiqueta,
  opciones,
  valor,
  disabled,
  onChange,
}: {
  readonly etiqueta: string;
  readonly opciones: readonly { valor: T; nombre: string }[];
  readonly valor: T;
  readonly disabled: boolean;
  readonly onChange: (valor: T) => void;
}) {
  return (
    <div className="qr-field">
      <span className="qr-field__label">{etiqueta}</span>
      <div className="diseno__formas">
        {opciones.map((opcion) => (
          <button
            key={opcion.valor}
            type="button"
            className={`diseno__forma${valor === opcion.valor ? ' diseno__forma--activa' : ''}`}
            aria-pressed={valor === opcion.valor}
            disabled={disabled}
            onClick={() => onChange(opcion.valor)}
          >
            {opcion.nombre}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Lo que ve quien no tiene el plan. Se muestra en vez de esconderlo. */
export function DesignUpsell() {
  return (
    <Callout tone="info" title="Disponible en los planes de pago">
      Con el diseño propio, el código lleva los colores de la marca, el logo en
      el centro y la forma que encaje con el cartel donde va impreso. Seguimos
      comprobando que se lea: lo que no permitimos es un código bonito que
      falle.
    </Callout>
  );
}
